// © 2026 AttainIP Engineering
/**
 * Route guard for the in-product enterprise Team/Admin section (RFC-0211/0212
 * A2). Gates on two things, both required:
 *  - appConfig.accountType === 'enterprise' (self-service builds never ship
 *    this section at all)
 *  - the current user holds the org_admin role (RFC-0121)
 *
 * A self-service user or a non-admin enterprise user is redirected away
 * before the section — or its route — ever renders. This mirrors the
 * RFC-0212 A2 security correction that keeps org_admin out of the
 * platform_admin-only ops console: org_admin self-management belongs here,
 * in the product, scoped to the firm admin's own firm only.
 *
 * Nest inside <RequireAuth> — this guard assumes `user` is already populated.
 */
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { appConfig } from '../../config/appConfig';
import { useAuthStore, isOrgAdmin } from '../../store/auth';

interface RequireOrgAdminProps {
  children: ReactNode;
}

export function RequireOrgAdmin({ children }: RequireOrgAdminProps) {
  const user = useAuthStore((s) => s.user);

  if (appConfig.accountType !== 'enterprise' || !isOrgAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
